
import { computed, onBeforeUpdate, onMounted, ref } from 'vue';
import { ConditionType, NodeType } from '@amplify/amp-module';
import { CONDITION_NODE_PORT_GROUPS } from '@/constants/amp';
import { NodeSizes } from '@/constants/builder';
import { GetAmpStorage, GetAvailableAccountKeys, GetStorageKeyValues } from '@/constants/getters';
import NodeAction from '@/components/graph/common/NodeAction';
import NodeWrapper from '@/components/graph/common/NodeWrapper';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { useNodeUtils } from '@/composables/useNodeUtils';

export default {
  components: {
    NodeAction,
    NodeWrapper,
  },

  props: {
    context: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  setup() {
    const nodeType = NodeType.condition;

    const { store } = useStoreInNodes();
    const { ampData, uiData, nodeId, nodeRef, nodeSelf, canEditAmp } = useNodeUtils({ nodeType });

    const nodeActions = ref({});

    const getStorageValues = (key) => store.value.getters[`ampStore/${GetStorageKeyValues}`](key);

    const crmAccountKeys = computed(() => store.value.getters[`crmStore/${GetAvailableAccountKeys}`]);
    const storage = computed(() => store.value.getters[`ampStore/${GetAmpStorage}`]);
    const storageValue = computed(
      () =>
        storage.value?.[ampData.value.storageKey] ||
        crmAccountKeys.value.find((accountKey) => accountKey.name === ampData.value.storageKey)?.name ||
        ''
    );
    const conditionValueOptions = computed(() => getStorageValues(ampData.value.storageKey));
    const conditionTypes = computed(() => Object.entries(ConditionType));

    const showConditionSelect = computed(
      () =>
        ampData.value.storageKey &&
        ampData.value.condition &&
        ![ConditionType.hasValue, ConditionType.hasNotValue].includes(ampData.value.condition)
    );

    const conditionText = computed(() => {
      const { condition, conditionValue } = ampData.value;

      let text = `If ${storageValue.value || '______'}`;

      if (storageValue.value) {
        text += ` ${condition || '__'}`;
      }

      if (condition && showConditionSelect.value) {
        text += ` ${conditionValue || '________'}`;
      }

      return text;
    });

    const isDone = computed(
      () =>
        storageValue.value && ampData.value.condition && (!showConditionSelect.value || !!ampData.value.conditionValue)
    );

    onMounted(() => {
      // create ports
      nodeSelf.value.prop('ports/groups', CONDITION_NODE_PORT_GROUPS);
      nodeSelf.value.prop('ports/items', [
        {
          id: `${nodeId.value}_port_true`,
          group: 'condition',
          args: { dy: 10 },
        },
        {
          id: `${nodeId.value}_port_false`,
          group: 'condition',
          args: { dy: 10 },
        },
      ]);
    });

    // make sure to reset the node actions refs before each update
    onBeforeUpdate(() => {
      nodeActions.value = {};
    });

    const onActionClick = (condition) => {
      if (!canEditAmp.value) {
        return;
      }

      (nodeActions.value[condition]?.[0] || nodeActions.value[condition]).onActionClick();
    };

    const resizeNode = (key) => {
      if (!ampData.value[key]) {
        const { width, height } = nodeSelf.value.size();

        if (key === 'storageKey' && height === NodeSizes.condition.height + 10) {
          return nodeSelf.value.resize(width, height + 34);
        }

        if (key === 'condition' && height === NodeSizes.condition.height + 44) {
          return nodeSelf.value.resize(width, height + 32);
        }
      }
    };

    const handleStorageValueChange = (storageKey) => {
      if (!canEditAmp.value) {
        return;
      }

      resizeNode('storageKey');
      ampData.value = { ...ampData.value, storageKey };
    };

    const handleConditionChange = (condition) => {
      if (!canEditAmp.value) {
        return;
      }

      resizeNode('condition');
      ampData.value = { ...ampData.value, condition };
    };

    const handleConditionValueChange = (conditionValue) => {
      if (!canEditAmp.value) {
        return;
      }

      resizeNode('condition');
      ampData.value = { ...ampData.value, conditionValue };
    };

    const branchHasChildren = (condition) => uiData.value?.children?.[`${condition}Branch`];

    return {
      ampData,
      canEditAmp,
      conditionText,
      conditionTypes,
      conditionValueOptions,
      crmAccountKeys,
      isDone,
      nodeActions,
      nodeId,
      nodeRef,
      nodeType,
      showConditionSelect,
      storage,
      storageValue,
      uiData,
      branchHasChildren,
      onActionClick,
      handleConditionChange,
      handleConditionValueChange,
      handleStorageValueChange,
    };
  },

  created() {
    // Updating the $vuetify key for node components to get the reference to the Vuetify context of the App
    this.$vuetify = this.context?.vuetify?.framework || this.$vuetify;
  },
};
